import { useMutation, ApolloError } from '@apollo/client'

import { MUTATION_UPDATE_PROJECT } from '@libs/apollo/mutations'

import { Project, ProjectUpdateInput, MutationUpdateProjectArgs, ProjectFileType, RemoteFile, LocalFile } from '@types'

import { useCreateProjectFile } from './create-project-file.hook'
import { useDeleteProjectFile } from './delete-project-file.hook'

interface Data {
  updateProject: Project
}

export const useUpdateProject = (): {
  updateProject: (
    input: ProjectUpdateInput,
    photos: (LocalFile | RemoteFile)[],
    plans: (LocalFile | RemoteFile)[],
  ) => void
  loading: boolean
  error: ApolloError | undefined
  data?: Data | null
} => {
  const [updateProject, { loading, error, data }] = useMutation<Data, MutationUpdateProjectArgs>(
    MUTATION_UPDATE_PROJECT,
    { notifyOnNetworkStatusChange: true },
  )

  const { createProjectFile } = useCreateProjectFile()
  const { deleteProjectFile } = useDeleteProjectFile()

  const _updateProject = async (
    input: ProjectUpdateInput,
    photos: (LocalFile | RemoteFile)[],
    _plans: (LocalFile | RemoteFile)[],
  ) => {
    const remoteDeletePhotos: RemoteFile[] = photos.filter(
      (photo) => Object.hasOwn(photo, 'deleted') && (photo as RemoteFile).deleted == true,
    ) as RemoteFile[]

    const localPhotos: LocalFile[] = photos.filter((photo) => Object.hasOwn(photo, 'data')) as LocalFile[]

    await Promise.all(remoteDeletePhotos.map((file) => deleteProjectFile({ projectFileId: file.id })))

    await Promise.all(
      localPhotos.map(async (file) => {
        await createProjectFile(
          {
            projectId: input.id,
            type: ProjectFileType.TypePhoto,
            name: file.name,
          },
          file,
        )
      }),
    )

    await updateProject({ variables: { input } })
  }

  return { updateProject: _updateProject, loading, error, data }
}
